import { clamp, min } from 'lodash-es';
import { forwardRef, useDeferredValue } from 'react';
import Modal, { type ModalAction } from '@/components/common/modal/modal';
import MessageListItem from '@/components/conversations/conversation-list/message-list-item/message-list-item';
import useMessageInfo from '@/components/conversations/conversation-list/message-list-item/use-message-info';
import useConversationStore from '@/hooks/conversations/use-conversation-store';
import { useIsDesktop } from '@/hooks/use-user-platform';
import cn from '@/utils/merge-class-names';
import ConversationMenu from './conversation-menu';

interface Props {
  onClose: () => void;
  position: [number, number] | null | undefined;
}

const ConversationMenuModal = forwardRef<ModalAction, Props>(({ onClose, position }, ref) => {
  const { selectedMessages } = useConversationStore();
  const message = useDeferredValue(selectedMessages.at(0));
  const { isMine } = useMessageInfo(message);
  const isDesktop = useIsDesktop();

  if (isDesktop && message && position) {
    const [posX, posY] = position;
    const maxWidth = window.innerWidth;
    const maxHeight = window.innerHeight;
    const ratioWidth = min([1, 1920 / window.innerWidth]) as number;
    const x = clamp(posX, 72, maxWidth - 72) * ratioWidth;
    const y = clamp(posY, 80, maxHeight - 80);

    return (
      <div
        className={cn('absolute z-50', {
          '-translate-x-full': x >= maxWidth * ratioWidth - 144,
          '-translate-y-full': y >= maxHeight - 208,
        })}
        style={{ left: x, top: y }}>
        <ConversationMenu
          message={message}
          onClose={onClose}
        />
      </div>
    );
  }

  return (
    <Modal
      ref={ref}
      className={cn(isMine ? 'items-end' : 'items-start')}
      contentClassName='select-none w-fit rounded-none bg-transparent p-0 shadow-none'>
      <div
        className='absolute inset-0'
        onPointerDown={() => onClose()}
      />

      <div className={cn('flex flex-col gap-4', isMine ? 'items-end' : 'items-start')}>
        {message && (
          <>
            <MessageListItem
              className='my-0 px-0 z-20 pointer-events-none'
              message={message}
              showHeader={false}
              type='quote'
            />
            <ConversationMenu
              message={message}
              onClose={onClose}
            />
          </>
        )}
      </div>
    </Modal>
  );
});

ConversationMenuModal.displayName = 'ConversationMenuModal';

export default ConversationMenuModal;
